import React, { useState, useCallback } from 'react';
import Modal from 'react-modal';
import { Spinner } from './Spinner';
import { translate } from '../Services/translation';
import { post } from '../Services/http';

const GeneralContactModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [organizationNumber, setorganizationNumber] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const fields = [
        {
            id: 'companyName',
            value: companyName,
            onChange: setCompanyName,
            required: false,
            autocomplete: 'organization',
        },
        {
            id: 'organizationNumber',
            value: organizationNumber,
            onChange: setorganizationNumber,
            required: false,
            autocomplete: 'off',
        },
        {
            id: 'email',
            value: email,
            onChange: setEmail,
            required: true,
            type: 'email',
            autocomplete: 'email',
        },
        {
            id: 'name',
            value: name,
            onChange: setName,
            required: true,
            autocomplete: 'name',
        },
        {
            id: 'phoneNumber',
            value: phoneNumber,
            onChange: setPhoneNumber,
            required: false,
            type: 'phone',
            autocomplete: 'tel',
        },
        {
            id: 'message',
            value: message,
            onChange: setMessage,
            required: true,
            tagType: 'textarea',
            autocomplete: 'off',
        },
    ];

    const customStyles = {
        overlay: {
            background: 'rgba(133, 133, 133, 0.6)',
            zIndex: 2003,
        },
    };

    const resetAndCloseModal = () => {
        setCompanyName('');
        setorganizationNumber('');
        setName('');
        setEmail('');
        setPhoneNumber('');
        setMessage('');
        setErrorMessage('');
        setSuccessMessage('');
        setIsLoading(false);
        setShowModal(false);
    };

    const submitForm = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const data = {
            companyName: companyName,
            organizationNumber: organizationNumber,
            email: email,
            name: name,
            phoneNumber: phoneNumber,
            message: message,
        };

        return post('/api/customersupport/contact', data)
            .then(() => {
                setSuccessMessage(translate('contact-form.success-message'));
                setTimeout(() => {
                    resetAndCloseModal();
                }, 3000);
            })
            .catch((ex) => {
                console.error('Failed to send notification to support', ex);
                setErrorMessage(translate('contact-form.error-message'));
                setIsLoading(false);
            });
    };

    return (
        <div className="contact-form">
            <a href="#">
                <i className="contact-form__icon"></i>
                <span onClick={() => setShowModal(!showModal)}>
                    {translate('general.contactform.link')}
                </span>
            </a>

            <Modal
                ariaHideApp={false}
                preventScroll={true}
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
                style={customStyles}
                shouldCloseOnOverlayClick={false}
                className="contact-form__modal-content"
            >
                <ContactForm
                    onSubmit={(formData) => submitForm(formData)}
                    onClose={() => setShowModal(false)}
                    fields={fields}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    successMessage={successMessage}
                />
            </Modal>
        </div>
    );
};

const ContactForm = ({
    fields,
    onSubmit,
    onClose,
    isLoading,
    errorMessage,
    successMessage,
}) => {
    const getButtonStyle = useCallback(() => {
        let style = 'contact-form__button';
        if (isLoading) {
            style += ' contact-form__button--disabled';
        }
        if (successMessage) {
            style += ' contact-form__button--success';
        }
        return style;
    }, [isLoading, successMessage]);

    return (
        <div className="contact-form__content-wrapper">
            <button
                onClick={() => onClose()}
                className="contact-form__close-btn"
            >
                &times;
            </button>

            <h1 className="contact-form__title">
                {translate('contact-form.title')}
            </h1>

            <form
                onSubmit={(e) => {
                    onSubmit(e);
                }}
                className="contact-form__forms-container"
            >
                {fields.map((field) =>
                    InputField(
                        field.id.toLowerCase(),
                        field.value,
                        field.onChange,
                        field.required,
                        field.tagType,
                        field.type,
                        field.autoComplete
                    )
                )}

                <p>{translate('contact-form.field-required-text')}</p>

                {errorMessage && (
                    <p className="contact-form__message--error">
                        {errorMessage}
                    </p>
                )}

                {successMessage && (
                    <p className="contact-form__message--success">
                        {successMessage}
                    </p>
                )}

                <button
                    className={getButtonStyle()}
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        translate('contact-form.submit-button-text')
                    )}
                </button>
            </form>
        </div>
    );
};

const InputField = (
    id,
    value,
    onChange,
    required = false,
    tagType = 'input',
    type = 'text',
    autoComplete = 'off'
) => {
    return (
        <div key={id} className="contact-form__form-control">
            {tagType === 'input' && (
                <input
                    className="contact-form__form-input"
                    id={id}
                    name={id}
                    placeholder=" "
                    type={type}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    required={required}
                    autoComplete={autoComplete}
                />
            )}
            {tagType === 'textarea' && (
                <textarea
                    className="contact-form__form-input"
                    id={id}
                    name={id}
                    placeholder=" "
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    required={required}
                    autoComplete={autoComplete}
                />
            )}
            <label htmlFor={id}>
                {translate(`contact-form.${id}`)}
                {required && (
                    <span className="contact-form__required-asterisk">*</span>
                )}
            </label>
        </div>
    );
};

export default GeneralContactModal;
